import { z } from 'zod';
import { Email, Password } from './auth.js';
import { InviteJoiner } from './project-invite.js';
/**
 * Cộng tác viên ngoài tổ chức — docs/plan-guest-collaborator.md, docs/plan-personal-space.md.
 *
 * `GuestProjectView` (trang `/work`) đã bỏ ở APM-PSPACE-g: khách có `project:read` nên dùng
 * trang dự án thường trong vỏ hạn chế.
 */
/**
 * PM mời cộng tác viên ngoài bằng email — APM-GUEST-e. Người được mời vào dự án
 * **ngay** (PM tự mời = đã duyệt), với vai "Cộng tác viên ngoài".
 *
 * `roleId` — APM-PSPACE-l: the role picked on the invites card. Omitted / `null` =
 * "Cộng tác viên ngoài", as before. Must belong to the project and fit the guest
 * ceiling (`GUEST_ROLE_TOO_WIDE` otherwise).
 */
export const InviteGuestInput = z.object({
    email: z.email().max(320),
    displayName: z.string().trim().min(1).max(200),
    roleId: z.uuid().nullable().optional(),
});
/**
 * `setPasswordUrl` chỉ có trong phản hồi này, **đúng một lần** — cùng luật với
 * lời mời khách hàng. `null` khi email đã có tài khoản: họ đã có mật khẩu.
 */
export const InviteGuestResult = z.object({
    userId: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    setPasswordUrl: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
});
/** Một cộng tác viên ngoài đang ở trong dự án. */
export const ProjectGuest = z.object({
    userId: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    roleName: z.string(),
    /** Việc chưa xong đang giao cho họ trong dự án — gỡ thì phải chuyển trước. */
    openTaskCount: z.number().int().nonnegative(),
    joinedAt: z.iso.datetime(),
});
// ── Link mời + đơn chờ duyệt — APM-GUEST-f, kế hoạch §4.6 ────────────────────
//
// Khuôn `client-invite.ts` (APM-CLINV-b), đổi khách hàng → cộng tác viên ngoài.
// Link chỉ cấp quyền **gửi đơn**; vào dự án là khi PM bấm Duyệt.
/** Bảy ngày — cùng con số với link mời khách hàng, dự án và tổ chức. */
export const GUEST_INVITE_TTL_DAYS = 7;
/** Link đang sống của dự án — bọc trong `{ invite }`, "chưa tạo link" là một giá trị. */
export const GuestInviteState = z.object({
    /**
     * `false` in the demo org: it takes no outsiders (APM-DEMO-out, 403 `DEMO_ORG_NO_OUTSIDERS`), so
     * the card shows why instead of buttons that only fail. Absent on create / role-change replies.
     */
    outsidersAllowed: z.boolean().optional(),
    invite: z
        .object({
        url: z.string(),
        expiresAt: z.iso.datetime(),
        createdAt: z.iso.datetime(),
        /** Số người đã gửi đơn qua link này. */
        acceptedCount: z.number().int().nonnegative(),
        /**
         * Role a request through this link is approved with by default — APM-PSPACE-l.
         * `null` = the project's "Cộng tác viên ngoài" role (as before).
         */
        roleId: z.uuid().nullable(),
    })
        .nullable(),
});
/** Một link mời cộng tác viên còn sống — APM-INVL-a (nhiều link song song, theo id). */
export const GuestInviteItem = z.object({
    id: z.uuid(),
    url: z.string(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    acceptedCount: z.number().int().nonnegative(),
    roleId: z.uuid().nullable(),
    joiners: z.array(InviteJoiner),
});
export const GuestInviteList = z.object({
    /** `false` ở org demo — không nhận người ngoài (APM-DEMO-out). */
    outsidersAllowed: z.boolean(),
    invites: z.array(GuestInviteItem),
});
/**
 * Create a guest link with a default role — APM-PSPACE-l. Omitted / `null` = "Cộng
 * tác viên ngoài". The role must belong to the project and fit the guest ceiling.
 * `.default({})`: callers from before this field send no body.
 */
export const CreateGuestInviteInput = z
    .object({ roleId: z.uuid().nullable().optional() })
    .default({});
/** Token đi trong **body**, kể cả lời gọi chỉ đọc — URL của API vào access log của proxy. */
export const GuestInviteTokenInput = z.object({
    token: z.string().trim().min(1).max(200),
});
/** Xem trước: link này mời vào dự án nào. Link hỏng ⇒ 404. */
export const GuestInvitePreview = z.object({
    orgName: z.string(),
    projectKey: z.string(),
    projectName: z.string(),
});
/** Người chưa có tài khoản đăng ký bằng link — tài khoản `GUEST` + đơn chờ duyệt. */
export const GuestInviteRegisterInput = z.object({
    token: z.string().trim().min(1).max(200),
    displayName: z.string().trim().min(1, 'Tên hiển thị là bắt buộc').max(200),
    email: Email,
    password: Password,
});
/**
 * Kết cục của lượt xin vào bằng link khi **đã** đăng nhập.
 *
 * - `ok` — đơn mới, chờ PM duyệt. `pending` — đã gửi rồi, không đơn thứ hai.
 * - `already_member` — đã ở trong dự án.
 * - `staff` — nhân sự của tổ chức: link khách không đổi vai của nhân sự.
 * - `client_account` — đang là khách hàng của tổ chức (một tư cách một vai, kế hoạch §8).
 * - `demo_account` — tài khoản demo không vào tổ chức thật (APM-DEMO-inv).
 */
export const GuestInviteApplyStatus = z.enum([
    'ok',
    'pending',
    'already_member',
    'staff',
    'client_account',
    'demo_account',
]);
export const GuestInviteApplyResult = z.object({
    status: GuestInviteApplyStatus,
    projectName: z.string(),
});
/** Một đơn đang chờ PM duyệt. */
export const ProjectGuestRequestItem = z.object({
    id: z.uuid(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    createdAt: z.iso.datetime(),
    /**
     * Role an approval without a pick gives — the live link's default role
     * (APM-PSPACE-l). `null` = "Cộng tác viên ngoài". The web preselects it.
     */
    roleId: z.uuid().nullable(),
});
/**
 * Name of the project role a guest gets when nobody picks one — created on first
 * use per project (`ensureGuestRole`, API). In contracts so the web can preselect
 * that role when approving a request (APM-PSPACE-k). It is a **data** name stored
 * in `project_role.name`, not a display string: changing it here forks every
 * project that already has the role.
 */
export const GUEST_PROJECT_ROLE_NAME = 'Cộng tác viên ngoài';
/**
 * PM approves a guest request, optionally choosing the project role — APM-PSPACE-k,
 * docs/plan-personal-space.md §3.9. The role must belong to the project and fit the
 * guest ceiling (`GUEST_ROLE_TOO_WIDE` otherwise).
 *
 * - omitted — the live guest link's default role (APM-PSPACE-l); no link or no
 *   default ⇒ "Cộng tác viên ngoài".
 * - `null` — "Cộng tác viên ngoài" (created on first use), whatever the link says.
 *
 * `.default({})`: callers from before this field send no body at all.
 */
export const AcceptGuestRequestInput = z
    .object({ roleId: z.uuid().nullable().optional() })
    .default({});
/** Từ chối kèm lý do (tuỳ chọn) — khách đọc ở `/work`. `.trim()` trước `.max()`. */
export const DeclineGuestRequestInput = z.object({
    note: z.string().trim().max(500).optional(),
});
/** Đơn của chính người gọi — dải "đang chờ duyệt" ở `/work`. */
export const MyGuestRequest = z.object({
    id: z.uuid(),
    projectName: z.string(),
    orgName: z.string(),
    status: z.enum(['pending', 'declined']),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
//# sourceMappingURL=guest.js.map